import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {SKU_PRIORITY_ORDER, type SkuType} from '@github-ui/copilot-chat/utils/sku-types'
import {useState} from 'react'

import {SKU_BANNER_CONFIGS} from './config'

export interface SkuBannerVisibility {
  visible: boolean
  onDismiss: () => void
}

export interface SkuBannerState {
  activeSku: SkuType | null
  visibility: Record<SkuType, SkuBannerVisibility>
}

const getInitialVisibilityState = (): Record<SkuType, boolean> => {
  const initialState: Record<SkuType, boolean> = Object.create(null)

  for (const skuType of SKU_PRIORITY_ORDER) {
    initialState[skuType] = copilotLocalStorage.getSkuSuccessBannerFlag(skuType) ?? false
  }

  return initialState
}

/**
 * Hook for managing SKU success banner visibility and dismissal.
 */
export function useSkuSuccessBanners(): SkuBannerState {
  const [visibilityState, setVisibilityState] = useState<Record<SkuType, boolean>>(getInitialVisibilityState)

  // Determine which SKU should be active based on priority order
  const activeSku = SKU_PRIORITY_ORDER.find(skuType => visibilityState[skuType]) || null

  // Create visibility objects for each SKU
  const visibility: Record<SkuType, SkuBannerVisibility> = {} as Record<SkuType, SkuBannerVisibility>

  for (const skuType of SKU_PRIORITY_ORDER) {
    const config = SKU_BANNER_CONFIGS[skuType]
    const relatedFlags = new Set<SkuType>([skuType, ...(config?.clearFlagsOnDismiss ?? [])])

    visibility[skuType] = {
      visible: activeSku === skuType,
      onDismiss: () => {
        for (const flag of relatedFlags) {
          copilotLocalStorage.removeSkuSuccessBannerFlag(flag)
        }

        setVisibilityState(previousState => {
          const nextState = {...previousState}

          for (const flag of relatedFlags) {
            nextState[flag] = false
          }

          return nextState
        })
      },
    }
  }

  return {
    activeSku,
    visibility,
  }
}
